<template>
  <card-dialog
    v-model="modalVisible"
    max-width="700px"
    :title="'Edit player details - ' + player.serial"
  >
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validationObserver">
        <div class="mb-2 font-weight-bold">
          Player Name
        </div>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="playerDirty.description"
            outlined
            :error-messages="errors"
            placeholder="Enter player name"
          />
        </validation-provider>

        <div class="mb-2 font-weight-bold">
          Client
        </div>
        <v-autocomplete
          v-model="playerDirty.client_id"
          solo
          flat
          outlined
          persistent-hint
          :items="clients.data"
          :loading="clients.loading"
          clearable
          item-text="name"
          item-value="id"
          placeholder="Select client this player belongs to"
        />

        <!--<editor-fold desc="Static network">-->
        <v-row align="center">
          <v-col class="font-weight-bold">
            Network DHCP
          </v-col>
          <v-col cols="8">
            <v-btn-toggle
              v-model="playerDirty.is_static_network"
              color="primary"
              mandatory
              class="row no-gutters"
              style="width: 100%"
            >
              <v-btn
                :value="false"
                large
                class="col text-none"
              >
                DHCP
              </v-btn>
              <v-btn
                :value="true"
                large
                class="col text-none"
              >
                Static (no DHCP)
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <div v-if="playerDirty.is_static_network && playerDirty.static_network_configuration">
          <v-row no-gutters>
            <v-col cols="8">
              <div class="mb-2 font-weight-bold">
                IP address
              </div>
              <validation-provider
                v-slot="{ errors }"
                rules="required|ip"
              >
                <v-text-field
                  v-model="playerDirty.static_network_configuration.ipaddress"
                  outlined
                  :error-messages="errors"
                  placeholder="Enter static IP address"
                />
              </validation-provider>
            </v-col>
            <v-col class="pl-4">
              <div class="mb-2 font-weight-bold">
                Subnet mask
              </div>
              <validation-provider
                v-slot="{ errors }"
                rules="required|min_value:16|max_value:30"
              >
                <v-text-field
                  v-model="playerDirty.static_network_configuration.subnetmask"
                  outlined
                  :error-messages="errors"
                  hint="16 - 30, usually 24"
                  placeholder="e.g. 24"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <div class="mb-2 font-weight-bold">
            Gateway
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required|ip"
          >
            <v-text-field
              v-model="playerDirty.static_network_configuration.gateway"
              outlined
              :error-messages="errors"
              placeholder="Enter gateway IP"
            />
          </validation-provider>

          <v-row
            no-gutters
            align="center"
            class="mb-2"
          >
            <v-col>
              <div class="font-weight-bold">
                DNS servers
              </div>
            </v-col>
            <v-col cols="auto">
              <v-btn
                small
                text
                color="primary"
                @click="playerDirty.static_network_configuration.dns.push('')"
              >
                <v-icon
                  left
                  small
                >
                  fa fa-plus
                </v-icon>
                Add
              </v-btn>
            </v-col>
          </v-row>

          <validation-provider
            v-for="(dns, index) in playerDirty.static_network_configuration.dns"
            :key="'dns_' + index"
            v-slot="{ errors }"
            rules="required|ip"
          >
            <v-text-field
              v-model="playerDirty.static_network_configuration.dns[index]"
              outlined
              :error-messages="errors"
              placeholder="Enter DNS server"
              :append-icon="playerDirty.static_network_configuration.dns.length > 1 ? 'fa fa-times' : undefined"
              @click:append="playerDirty.static_network_configuration.dns.splice(index, 1)"
            />
          </validation-provider>
        </div>
        <!--</editor-fold>-->

        <!--<editor-fold desc="Hostname">-->
        <v-row align="center">
          <v-col class="font-weight-bold">
            Hostname
          </v-col>
          <v-col cols="8">
            <v-btn-toggle
              v-model="playerDirty.has_manual_hostname"
              color="primary"
              mandatory
              class="row no-gutters"
              style="width: 100%"
            >
              <v-btn
                :value="false"
                large
                class="col text-none"
              >
                Default
              </v-btn>
              <v-btn
                :value="true"
                large
                class="col text-none"
              >
                Manual
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <div v-if="playerDirty.has_manual_hostname">
          <validation-provider
            v-slot="{ errors }"
            rules="required|alpha_num"
          >
            <v-text-field
              v-model="playerDirty.hostname"
              outlined
              :error-messages="errors"
              placeholder="Enter player hostname"
            />
          </validation-provider>
        </div>
        <!--</editor-fold>-->

        <!--<editor-fold desc="Wifi">-->
        <v-row align="center">
          <v-col class="font-weight-bold">
            WiFi enabled
          </v-col>
          <v-col cols="8">
            <v-btn-toggle
              v-model="playerDirty.is_wifi_enabled"
              color="primary"
              mandatory
              class="row no-gutters"
              style="width: 100%"
            >
              <v-btn
                :value="false"
                large
                class="col text-none"
              >
                No, LAN only
              </v-btn>
              <v-btn
                :value="true"
                large
                class="col text-none"
              >
                Yes, use WiFi
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <div v-if="playerDirty.is_wifi_enabled && playerDirty.wifi_configuration">
          <div class="mb-2 font-weight-bold">
            SSID
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="playerDirty.wifi_configuration.ssid"
              outlined
              :error-messages="errors"
              placeholder="Enter wifi network SSID"
            />
          </validation-provider>

          <v-switch
            v-model="playerDirty.wifi_configuration.is_hidden"
            label="Hidden SSID"
            class="mx-0 mt-0 mb-3 pa-0"
          />

          <div class="mb-2 font-weight-bold">
            Password
          </div>
          <validation-provider v-slot="{ errors }">
            <v-text-field
              v-model="playerDirty.wifi_configuration.password"
              outlined
              :error-messages="errors"
              placeholder="Enter wifi network password"
              hint="WPA-PSK password or leave empty if it is a guest network"
            />
          </validation-provider>
        </div>
        <!--</editor-fold>-->

        <!--<editor-fold desc="NTP config">-->
        <v-row align="center">
          <v-col class="font-weight-bold">
            NTP servers
          </v-col>
          <v-col cols="8">
            <v-btn-toggle
              v-model="playerDirty.is_ntp_manual"
              color="primary"
              mandatory
              class="row no-gutters"
              style="width: 100%"
            >
              <v-btn
                :value="false"
                large
                class="col text-none"
              >
                Automatic
              </v-btn>
              <v-btn
                :value="true"
                large
                class="col text-none"
              >
                Manual
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <div v-if="playerDirty.is_ntp_manual && playerDirty.ntp_configuration">
          <v-row
            no-gutters
            align="center"
            class="mb-2"
          >
            <v-spacer />
            <v-col cols="auto">
              <v-btn
                small
                text
                color="primary"
                @click="playerDirty.ntp_configuration.servers.push('')"
              >
                <v-icon
                  left
                  small
                >
                  fa fa-plus
                </v-icon>
                Add
              </v-btn>
            </v-col>
          </v-row>

          <validation-provider
            v-for="(ntp, index) in playerDirty.ntp_configuration.servers"
            :key="'ntp_' + index"
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="playerDirty.ntp_configuration.servers[index]"
              outlined
              :error-messages="errors"
              placeholder="Enter NTP server"
              append-icon="fa fa-times"
              @click:append="playerDirty.ntp_configuration.servers.splice(index, 1)"
            />
          </validation-provider>
        </div>
        <!--</editor-fold>-->

        <!--<editor-fold desc="Proxy">-->
        <v-row align="center">
          <v-col class="font-weight-bold">
            Proxy
          </v-col>
          <v-col cols="8">
            <v-btn-toggle
              v-model="playerDirty.is_proxy_enabled"
              color="primary"
              mandatory
              class="row no-gutters"
              style="width: 100%"
            >
              <v-btn
                :value="false"
                large
                class="col text-none"
              >
                Disabled
              </v-btn>
              <v-btn
                :value="true"
                large
                class="col text-none"
              >
                Enabled
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <div v-if="playerDirty.is_proxy_enabled && playerDirty.proxy_configuration">
          <v-row no-gutters>
            <v-col cols="8">
              <div class="mb-2 font-weight-bold">
                Host address
              </div>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="playerDirty.proxy_configuration.host"
                  outlined
                  :error-messages="errors"
                  placeholder="Enter proxy host address"
                  hint="Hostname or IP address"
                />
              </validation-provider>
            </v-col>
            <v-col class="pl-4">
              <div class="mb-2 font-weight-bold">
                Port
              </div>
              <validation-provider
                v-slot="{ errors }"
                rules="required|min_value:1|max_value:65535"
              >
                <v-text-field
                  v-model="playerDirty.proxy_configuration.port"
                  outlined
                  :error-messages="errors"
                  placeholder="e.g. 3128"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <div class="mb-2 font-weight-bold">
            Username
          </div>
          <validation-provider v-slot="{ errors }">
            <v-text-field
              v-model="playerDirty.proxy_configuration.username"
              outlined
              :error-messages="errors"
              placeholder="Enter proxy username"
              hint="Leave empty if credentials are not needed"
            />
          </validation-provider>

          <div class="mb-2 font-weight-bold">
            Password
          </div>
          <validation-provider v-slot="{ errors }">
            <v-text-field
              v-model="playerDirty.proxy_configuration.password"
              outlined
              :error-messages="errors"
              placeholder="Enter proxy password"
              hint="Leave empty if credentials are not needed"
            />
          </validation-provider>
        </div>
        <!--</editor-fold>-->

        <!--<editor-fold desc="Download relay">-->
        <v-row align="center">
          <v-col class="font-weight-bold">
            Download relay
          </v-col>
          <v-col cols="8">
            <v-btn-toggle
              v-model="playerDirty.use_download_relay"
              color="primary"
              mandatory
              class="row no-gutters"
              style="width: 100%"
            >
              <v-btn
                :value="false"
                large
                class="col text-none"
              >
                Disabled
              </v-btn>
              <v-btn
                :value="true"
                large
                class="col text-none"
              >
                Enabled
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <div v-if="playerDirty.use_download_relay">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="playerDirty.download_relay"
              outlined
              :error-messages="errors"
              placeholder="e.g. http://download.relay.address"
            />
          </validation-provider>
        </div>
        <!--</editor-fold>-->
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-btn
          v-if="playerDirty.id"
          color="grey"
          outlined
          @click="toggleConfirmationDialog(true)"
        >
          <v-icon
            left
            small
          >
            fa fa-trash
          </v-icon>
          Delete
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="submitting"
          @click="submit()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </template>

    <confirm-delete-dialog
      :visible="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirm="deletePlayer()"
    />
  </card-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import PlayerModel from '@/models/PlayerModel'
import Players from '@/services/Players'
import Clients from '@/services/Clients'

export default {
  name: 'EditPlayerDialog',
  components: { ConfirmDeleteDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    player: {
      type: Object,
      default: () => cloneDeep(PlayerModel.emptyModel)
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      playerDirty: cloneDeep(this.player),
      submitting: false,
      deleting: false,
      clients: {
        loading: false,
        data: []
      }
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    modalVisible(value) {
      if (!value) return

      this.playerDirty = cloneDeep(this.player)

      this.prepareConfigurationFields()
    }
  },
  created() {
    this.fetchClients()

    this.prepareConfigurationFields()
  },
  methods: {
    prepareConfigurationFields() {
      if (!this.playerDirty.static_network_configuration) this.playerDirty.static_network_configuration = PlayerModel.emptyModel.static_network_configuration
      if (!this.playerDirty.wifi_configuration) this.playerDirty.wifi_configuration = PlayerModel.emptyModel.wifi_configuration
      if (!this.playerDirty.ntp_configuration) this.playerDirty.ntp_configuration = PlayerModel.emptyModel.ntp_configuration
      if (!this.playerDirty.proxy_configuration) this.playerDirty.proxy_configuration = PlayerModel.emptyModel.proxy_configuration
    },
    async submit() {
      if (!(await this.$refs.validationObserver.validate())) return

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        const result = await Players.update(this.playerDirty.id, this.playerDirty)

        this.$emit('player:updated', result)

        this.$store.dispatch('alert', {
          text: 'Player successfully updated',
          color: 'success'
        })

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There were some errors submitting player updates',
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deletePlayer() {
      this.deleting = true

      try {
        const result = await Players.delete([this.playerDirty.id])

        this.$store.dispatch('alert', {
          text: 'Successfully deleted',
          color: 'success'
        })

        this.modalVisible = false
        this.$emit('player:updated', result)
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.deleteConfirmationDialogVisible = false
        this.deleting = false
      }
    },
    async fetchClients() {
      this.clients.loading = true

      try {
        this.clients.data = (await Clients.fetch())
      } catch (e) {

      } finally {
        this.clients.loading = false
      }
    }
  }
}
</script>
